import { useEffect, useMemo, useState } from "react";

interface EventGalleryProps {
  featuredImageUrl?: string;
  galleryImageUrls?: string[];
  title: string;
}

function loadImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || 4,
        height: img.naturalHeight || 3,
      });
    };
    img.onerror = () => resolve({ width: 4, height: 3 });
    img.src = url;
  });
}

function EventGallery({ featuredImageUrl, galleryImageUrls, title }: EventGalleryProps) {
  const photos = useMemo(() => {
    const all = [
      ...(featuredImageUrl ? [featuredImageUrl] : []),
      ...(galleryImageUrls ?? []).filter(Boolean),
    ];
    return Array.from(new Set(all));
  }, [featuredImageUrl, galleryImageUrls]);

  const [index, setIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadImageSize(photos[0]).then((first) => {
      if (!cancelled) {
        setAspectRatio(first.width / first.height);
      }
    });

    if (photos.length <= 1) {
      return () => {
        cancelled = true;
      };
    }

    Promise.all(photos.map(loadImageSize)).then((sizes) => {
      if (cancelled) {
        return;
      }
      const fitAllRatio = Math.min(
        ...sizes.map(({ width, height }) => width / height)
      );
      setAspectRatio(fitAllRatio);
    });

    return () => {
      cancelled = true;
    };
  }, [photos]);

  if (photos.length === 0) {
    return null;
  }

  const current = photos[index];
  const hasMultiple = photos.length > 1;

  const goPrev = () => {
    setIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goNext = () => {
    setIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="event-gallery">
      <div
        className="event-gallery__viewer"
        style={
          aspectRatio
            ? ({ "--gallery-aspect": aspectRatio } as React.CSSProperties)
            : undefined
        }
      >
        <img src={current} alt={title} className="event-gallery__slide" />
        {hasMultiple && (
          <>
            <button
              type="button"
              className="event-gallery__nav event-gallery__nav--prev"
              onClick={goPrev}
              aria-label="Previous photo"
            >
              ←
            </button>
            <button
              type="button"
              className="event-gallery__nav event-gallery__nav--next"
              onClick={goNext}
              aria-label="Next photo"
            >
              →
            </button>
            <span className="event-gallery__counter">
              {index + 1} / {photos.length}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default EventGallery;
